import { useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = {
    CREATED: 'Creado',
    UPDATED: 'Actualizado',
    STATUS_CHANGED: 'Cambio de Estado',
    NOTE_ADDED: 'Nota Agregada',
    ALERT_TRIGGERED: 'Alerta Disparada',
    NOTIFICATION: 'Notificación',
    FILING: 'Presentación',
    OFFICIAL_COMMUNICATION: 'Oficio',
    RESPONSE: 'Respuesta'
};

export const CreateEventModal = ({ procedureId, token, open, onClose, onEventCreated }) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        event_type: 'UPDATED',
        event_date: new Date().toISOString().split('T')[0]
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title || !formData.event_date) {
            toast.error('Título y fecha son requeridos');
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${API}/procedures/${procedureId}/events`, {
                ...formData,
                event_date: new Date(formData.event_date).toISOString()
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success('Actuación registrada');
            // Check if timeline needs refresh - simple way is just call callback
            if (onEventCreated) onEventCreated();
            onClose();
            // Reset form
            setFormData({
                title: '',
                description: '',
                event_type: 'UPDATED',
                event_date: new Date().toISOString().split('T')[0]
            });
        } catch (error) {
            console.error('Error creating event:', error);
            toast.error('Error al registrar actuación');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Registrar Actuación</DialogTitle>
                    <DialogDescription>
                        Agrega un evento a la línea de tiempo del procedimiento.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="title">Título</Label>
                        <Input
                            id="title"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            placeholder="Ej. Recepción de oficio, Presentación de escrito..."
                            required
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="type">Tipo</Label>
                            <Select
                                value={formData.event_type}
                                onValueChange={(val) => setFormData({ ...formData, event_type: val })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="UPDATED">Actualización</SelectItem>
                                    <SelectItem value="NOTIFICATION">Notificación</SelectItem>
                                    <SelectItem value="FILING">Presentación</SelectItem>
                                    <SelectItem value="OFFICIAL_COMMUNICATION">Oficio</SelectItem>
                                    <SelectItem value="RESPONSE">Respuesta</SelectItem>
                                    <SelectItem value="NOTE">Nota</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="date">Fecha</Label>
                            <Input
                                id="date"
                                type="date"
                                value={formData.event_date}
                                onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                                required
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="description">Descripción (Opcional)</Label>
                        <Textarea
                            id="description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Detalles adicionales..."
                            rows={3}
                        />
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            Registrar
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
